import {
  Agreement,
  Commitment,
  Compensation,
  IAgreement,
  ICommitment,
  ICompensation,
  IOpportunityAttachments,
  IOpportunityDetail,
  IOpportunityLanguage,
  IOpportunityStrength,
  IOrganization,
  IPlace,
  IStartDate,
  ITimeframe,
  Member,
  OpportunityAttachments,
  OpportunityDetail,
  OpportunityLanguage,
  OpportunityStrength,
  Organization,
  Person,
  Place,
  SaveOpportunity,
  SaveOpportunityDeadline,
  SaveOpportunityLanguage,
  StartDate,
  StrengthExperienceSelection,
  Timeframe
} from "@/services/DTOs";
import { IPrefilledStatus, PrefilledStatus } from "@/services/DTOs/PrefilledStatus";

export enum OpportunityStatus {
  POSTED = "Posted",
  CLOSED = "Closed",
  DRAFT = "Draft"
}

export enum OpportunityRoleType {
  FULL_TIME = "full-time",
  FLEXIBLE_JOB = "flexible-job",
  INTERN = "intern"
}

export enum OpportunityServiceType {
  SELF_SERVICE = "self_service",
  PRIME = "prime",
  PRIME_RPO = "prime_rpo",
  PRIME_X = "prime_x",
  PRIME_PLUS = "prime_plus",
  ATS = "ats",
  AGILE = "agile",
  ESSENTIALS = "essentials",
  ENTERPRISE = "enterprise",
  STAFF_AUGMENTATION = "staff_augmentation",
  PRO = "pro",
  FREE = "free"
}

export enum OpportunityServiceCode {
  AGILE = 1,
  ATS = 8
}

export interface IOpportunityLocale {
  locale: string;
}

export interface IOpportunity {
  draft?: string;
  id: string;
  slug?: string;
  owner: Person;
  opportunity: string;
  objective: string;
  theme: string;
  commitment?: ICommitment;
  timeframe?: ITimeframe;
  timezones?: string[];
  startDate?: IStartDate;
  agreement?: IAgreement;
  openGraph?: string;
  completion?: number;
  strengths: IOpportunityStrength[];
  organizations: IOrganization[];
  languages: IOpportunityLanguage[];
  place: IPlace;
  sponsorVisa?: string;
  compensation?: ICompensation;
  details: IOpportunityDetail[];
  attachments: IOpportunityAttachments[];
  created: string | Date;
  deadline: string;
  stableOn?: string;
  members: Member[];
  boardVersion: number;
  externalInfoSourceUrl?: string;
  crawled?: boolean;
  template?: string;
  prefilledStatus?: IPrefilledStatus;
  status: string;
  locale: string;
  published: boolean;
  stats: {
    finishedApplications: number;
  };
  review: string;
  serviceTypes: ITorreServiceDefinition[];
  previousServices: ITorreServiceDefinition[];
  isTalentSeekerInactive?: boolean;
  messengerTopicsEnabled: boolean;
}

export interface ITorreServiceDefinition {
  name: string;
  code: number;
}

export class Opportunity implements IOpportunity {

  public static Test(): Opportunity {
    return new Opportunity({
      id: "1",
      objective: "Test Opportunity",
      theme: "default",
      owner: Person.Test(),
      opportunity: "",
      strengths: [],
      organizations: [],
      languages: [],
      place: Place.Test(),
      details: [],
      attachments: [],
      created: new Date(),
      deadline: "",
      members: [],
      boardVersion: 1,
      status: "open",
      locale: "en",
      published: true,
      stats: { finishedApplications: 0 },
      review: "",
      serviceTypes: [],
      previousServices: [],
      isTalentSeekerInactive: false,
      messengerTopicsEnabled: false
    });
  }

  public draft?: string;
  public id: string;
  public slug?: string;
  public friendlyId: string;
  public owner: Person;
  public opportunity: string;
  public objective: string;
  public theme: string;
  public commitment?: Commitment;
  public timeframe?: Timeframe;
  public timezones?: string[];
  public startDate?: StartDate;
  public agreement?: Agreement;
  public openGraph?: string;
  public completion?: number;
  public strengths: OpportunityStrength[];
  public organizations: Organization[];
  public languages: OpportunityLanguage[];
  public place: Place;
  public sponsorVisa?: string;
  public compensation?: Compensation;
  public details: OpportunityDetail[];
  public attachments: OpportunityAttachments[];
  public created: string | Date;
  public deadline: string;
  public stableOn?: string;
  public members: Member[];
  public boardVersion: number;
  public externalInfoSourceUrl?: string;
  public crawled?: boolean;
  public template?: string;
  public prefilledStatus?: PrefilledStatus;
  public status: string;
  public locale: string;
  public isTest: boolean;
  public published: boolean;
  public stats: {
    finishedApplications: number
  };
  public review: string;
  public serviceTypes: ITorreServiceDefinition[];
  public previousServices: ITorreServiceDefinition[];
  public services: number[];
  public isTalentSeekerInactive?: boolean;
  public messengerTopicsEnabled: boolean;

  constructor(data: IOpportunity) {
    this.draft = data.draft;
    this.id = data.id;
    this.slug = data.slug;
    this.friendlyId = `${data.id}${data.slug ? `-${data.slug}` : ""}`;
    this.owner = new Person(data.owner);
    this.opportunity = data.opportunity;
    this.objective = data.objective;
    this.theme = data.theme;
    this.commitment = data.commitment ? new Commitment(data.commitment) : undefined;
    this.timeframe = data.timeframe ? new Timeframe(data.timeframe) : undefined;
    this.timezones = data.timezones;
    this.startDate = data.startDate ? new StartDate(data.startDate) : undefined;
    this.agreement = data.agreement ? new Agreement(data.agreement) : undefined;
    this.openGraph = data.openGraph;
    this.completion = data.completion;
    this.strengths = data.strengths.map((strength) => new OpportunityStrength(strength));
    this.organizations = data.organizations.map((org) => new Organization(org));
    this.languages = data.languages.map((lang) => new OpportunityLanguage(lang));
    this.place = new Place(data.place);
    this.sponsorVisa = data.sponsorVisa;
    this.compensation = data.compensation ? new Compensation(data.compensation) : undefined;
    this.details = data.details;
    this.attachments = data.attachments;
    this.created = data.created;
    this.deadline = data.deadline;
    this.stableOn = data.stableOn;
    this.members = data.members.map((m) => new Member({ ...m }));
    this.boardVersion = data.boardVersion;
    this.externalInfoSourceUrl = data.externalInfoSourceUrl;
    this.crawled = data.crawled;
    this.template = data.template;
    this.prefilledStatus = data.prefilledStatus ? new PrefilledStatus(data.prefilledStatus) : undefined;
    this.status = data.status;
    this.locale = data.locale;
    this.isTest = data.objective.trim().startsWith("***");
    this.stats = data.stats;
    this.published = data.published;
    this.review = data.review;
    this.serviceTypes = data.serviceTypes;
    this.previousServices = data.previousServices;
    this.services = (data.serviceTypes || []).map((s) => s.code);
    this.isTalentSeekerInactive = data.isTalentSeekerInactive;
    this.messengerTopicsEnabled = data.messengerTopicsEnabled;
  }

  public toSaveOpportunity(): SaveOpportunity {
    return new SaveOpportunity({
      opportunity: this.opportunity,
      objective: this.objective,
      strengths: this.strengths.map((s) =>
        new StrengthExperienceSelection({id: s.code, term: s.name, experience: s.experience, locale: s.locale})
      ),
      organizations: this.organizations,
      languages: this.languages.map((l) =>
        new SaveOpportunityLanguage({code: l.language.code, name: l.language.name, fluency: l.fluency})
      ),
      commitment: this.commitment,
      timeframe: this.timeframe,
      timezones: this.timezones,
      startDate: this.startDate,
      place: this.place,
      sponsorVisa: this.sponsorVisa,
      compensation: this.compensation ? this.compensation : undefined,
      attachments: this.attachments,
      details: this.details,
      agreement: this.agreement,
      draftToken: undefined,
      deadline: new SaveOpportunityDeadline({ type: "never" }),
      members: this.members || [],
      prefilledStatus: this.prefilledStatus,
      locale: this.locale
    });
  }

  public getStatus(): string {
    if (!!this.draft || !this.published) {
      return "Draft";
    }

    if (this.deadline) {
      if (new Date() > new Date(this.deadline)) {
        return "Closed";
      }
    }
    return "Posted";
  }

  public isOpen(): boolean {
    return this.status !== "closed";
  }

  public isUpcoming(): boolean {
    return this.status === "opening-soon";
  }
}
